// PR-26: Gaps tab — vertical list of open gap-kind cards with three big
// tap targets per row (Approve / Discuss / Reject).
// PR-34: full GapLedger parity. Three sections like desktop:
//   1. NEEDS YOUR CALL — kind=gap cards, grouped by priority
//   2. STALE RIPPLE    — non-gap cards with stale=true
//   3. FRAME GAPS      — empty mece_required nodes from the active frame,
//                        passed in from MobileShell (PR-33's surfacing)
// 9a4535c port (mobile alignment): adds a fourth top section
//   0. OPEN QUESTIONS — kind=question cards (the "your todo" surface),
//                        each row gets [answer ↗] / [archive] tap targets.
//   Plus smart proposal labels (Add to OPEN vs Add to cards) and bulk
//   approve/dismiss with an Undo toast at the section header.
//
// Discuss now fires a real cross-tab handler in MobileShell that creates
// (or finds) a discuss thread, pins the gap, switches to the Chat tab,
// and fires the synthetic opener turn (mirrors desktop App.tsx flow).
//
// Audit button at the top is a stub for PR-34 — PR-35 wires it to the
// real Distill flow with mode='audit'.

import { useMemo, useState } from 'react'
import type { Card } from '../../api'
import type { FrameGapInfo } from '../CardsPanel'
import { KindPill } from './MobileCards'

interface MobileGapsProps {
  cards: Card[]
  /** PR-33 surface from MobileCards: the active frame's empty
   *  mece_required nodes, if any. Empty array when no frame is active. */
  frameGaps: FrameGapInfo[]
  onApprove: (cardId: string) => void | Promise<void>
  onReject: (cardId: string) => void | Promise<void>
  /** Discuss flow — creates/switches to a thread pinned to this gap and
   *  jumps to the Chat tab. Wired in MobileShell via onDiscussGap. */
  onDiscuss: (cardId: string) => void
  /** Open a thread to work the question through with the agent. Same
   *  shape as `onDiscuss` (MobileShell routes both through the
   *  generalised onDiscussGap). */
  onAnswerQuestion?: (cardId: string) => void
  /** Soft-hide a question — leaves wikilinks resolvable but drops it
   *  from OPEN QUESTIONS. */
  onArchiveQuestion?: (cardId: string) => void | Promise<void>
  /** Bulk-approve all pending proposals in one shot. Returns a Promise
   *  so the section header can spinner-flag while the parallel POSTs
   *  fly. The parent surfaces an Undo toast on success. */
  onApproveBatch?: (cardIds: string[]) => Promise<{ ok: string[]; failed: string[] }>
  /** Bulk-dismiss every pending proposal. No undo (rejection writes
   *  dismissed_gaps marks; reversing is more surgery than the savings
   *  warrant — desktop made the same call). */
  onRejectBatch?: (cardIds: string[]) => Promise<{ ok: string[]; failed: string[] }>
  onPeek: (cardId: string) => void
  /** PR-35-stub for now; PR-35 fires the Distill audit flow. */
  onAudit: () => void
}

const PRIORITY_ORDER = ['p0', 'p1', 'p2', 'p3'] as const
type PriorityKey = (typeof PRIORITY_ORDER)[number] | 'unsorted'

function gapPriority(c: Card): PriorityKey {
  const p = c.extra?.['priority']
  if (typeof p !== 'string') return 'unsorted'
  if ((PRIORITY_ORDER as readonly string[]).includes(p)) return p as PriorityKey
  return 'unsorted'
}

function deriveTitle(c: Card): string {
  if (c.title?.trim()) return c.title.trim()
  const firstLine = c.body
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0)
  if (!firstLine) return '(untitled)'
  return firstLine.length > 80 ? firstLine.slice(0, 80).trimEnd() + '…' : firstLine
}

function priorityLabel(p: PriorityKey): string {
  return p === 'unsorted' ? 'UNSORTED' : p.toUpperCase()
}

/** Read `extra.proposed_kind`, falling back to the legacy
 *  `proposes_kind` shape and the gap_kind=conflict → statement /
 *  default → question rule that mirrors the server's promote handler.
 *  Used to label the Approve button intelligently. */
function effectiveProposedKind(c: Card): string {
  const lit =
    (typeof c.extra?.['proposed_kind'] === 'string'
      ? (c.extra['proposed_kind'] as string)
      : null) ??
    (typeof c.extra?.['proposes_kind'] === 'string'
      ? (c.extra['proposes_kind'] as string)
      : null)
  if (lit) return lit
  const gapKind =
    typeof c.extra?.['gap_kind'] === 'string'
      ? (c.extra['gap_kind'] as string)
      : null
  return gapKind === 'conflict' ? 'statement' : 'question'
}

export function MobileGaps({
  cards,
  frameGaps,
  onApprove,
  onReject,
  onDiscuss,
  onAnswerQuestion,
  onArchiveQuestion,
  onApproveBatch,
  onRejectBatch,
  onPeek,
  onAudit,
}: MobileGapsProps) {
  // Skip archived cards across all sections — soft-hidden by the user
  // (or by `onArchiveQuestion`); they still exist for wikilink resolution
  // but shouldn't show up in "what needs attention".
  const visibleCards = useMemo(
    () => cards.filter((c) => !c.archived),
    [cards],
  )

  // ── Section 0 — OPEN QUESTIONS (kind=question) ──────────────────
  // Newest-first. Tiebreak by id so two questions with the same created_at
  // don't reshuffle on every render.
  const openQuestions = useMemo(() => {
    return visibleCards
      .filter((c) => c.kind === 'question')
      .sort((a, b) => {
        const cmp = (b.created_at ?? '').localeCompare(a.created_at ?? '')
        return cmp !== 0 ? cmp : b.card_id.localeCompare(a.card_id)
      })
  }, [visibleCards])

  // ── Section 1 — NEEDS YOUR CALL (gaps grouped by priority) ──────
  const gapGroups = useMemo(() => {
    const out: Record<PriorityKey, Card[]> = {
      p0: [],
      p1: [],
      p2: [],
      p3: [],
      unsorted: [],
    }
    for (const c of visibleCards) {
      if (c.kind !== 'gap') continue
      out[gapPriority(c)].push(c)
    }
    for (const key of Object.keys(out) as PriorityKey[]) {
      out[key].sort((a, b) => b.updated_at.localeCompare(a.updated_at))
    }
    return out
  }, [visibleCards])
  const allGaps = useMemo(
    () => Object.values(gapGroups).flat(),
    [gapGroups],
  )
  const hasGroupedPriority = useMemo(
    () =>
      PRIORITY_ORDER.some((p) => gapGroups[p].length > 0),
    [gapGroups],
  )

  // ── Section 2 — STALE RIPPLE ────────────────────────────────────
  const staleRipple = useMemo(
    () =>
      visibleCards
        .filter((c) => c.kind !== 'gap' && c.kind !== 'question' && c.stale)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [visibleCards],
  )

  // ── totals + audit availability ────────────────────────────────
  const totalCount =
    openQuestions.length +
    allGaps.length +
    staleRipple.length +
    frameGaps.length
  const auditEnabled = cards.length > 0

  // Bulk-action busy flag — disables both bulk buttons + the per-row
  // controls aren't reachable while the parallel POSTs fly.
  const [bulkBusy, setBulkBusy] = useState<null | 'approve' | 'reject'>(null)

  if (totalCount === 0) {
    return (
      <section className="mobile-gaps">
        <div className="mobile-gaps__head">
          <span className="mobile-gaps__total mono">CLEAR</span>
          <button
            type="button"
            className="mobile-gaps__audit"
            onClick={onAudit}
            disabled={!auditEnabled}
          >
            Audit gaps
          </button>
        </div>
        <div className="mobile-gaps__empty">
          Nothing to triage. Tap Audit to find more gaps in the conversation.
        </div>
      </section>
    )
  }

  // Smart bulk-approve label: "Add all (3→cards · 2→OPEN)" — mirrors
  // desktop's per-burst summary so the user knows the destination split
  // before they tap.
  const proposedKindCounts = (() => {
    let toOpen = 0
    let toCards = 0
    for (const g of allGaps) {
      if (effectiveProposedKind(g) === 'question') toOpen++
      else toCards++
    }
    return { toOpen, toCards }
  })()
  const bulkApproveLabel = (() => {
    const parts: string[] = []
    if (proposedKindCounts.toCards > 0) parts.push(`${proposedKindCounts.toCards}→cards`)
    if (proposedKindCounts.toOpen > 0) parts.push(`${proposedKindCounts.toOpen}→OPEN`)
    return parts.length > 0
      ? `Add all (${parts.join(' · ')})`
      : `Add all (${allGaps.length})`
  })()

  const handleBulkApprove = async () => {
    if (!onApproveBatch || allGaps.length === 0 || bulkBusy) return
    setBulkBusy('approve')
    try {
      await onApproveBatch(allGaps.map((g) => g.card_id))
    } finally {
      setBulkBusy(null)
    }
  }
  const handleBulkReject = async () => {
    if (!onRejectBatch || allGaps.length === 0 || bulkBusy) return
    setBulkBusy('reject')
    try {
      await onRejectBatch(allGaps.map((g) => g.card_id))
    } finally {
      setBulkBusy(null)
    }
  }

  return (
    <section className="mobile-gaps">
      <div className="mobile-gaps__head">
        <span className="mobile-gaps__total mono">{totalCount} OPEN</span>
        <button
          type="button"
          className="mobile-gaps__audit"
          onClick={onAudit}
          disabled={!auditEnabled}
        >
          Audit gaps
        </button>
      </div>

      {/* Section 0: OPEN QUESTIONS — your "still unresolved" todo list */}
      {openQuestions.length > 0 && (
        <div className="mobile-gaps__section">
          <h2 className="mobile-gaps__section-title">
            OPEN QUESTIONS · {openQuestions.length}
          </h2>
          {openQuestions.map((q) => (
            <OpenQuestionRow
              key={q.card_id}
              card={q}
              onPeek={onPeek}
              onAnswer={onAnswerQuestion}
              onArchive={onArchiveQuestion}
            />
          ))}
        </div>
      )}

      {/* Section 1: NEEDS YOUR CALL */}
      {allGaps.length > 0 && (
        <div className="mobile-gaps__section">
          <h2 className="mobile-gaps__section-title">
            NEEDS YOUR CALL · {allGaps.length}
          </h2>
          {/* Bulk action row — only shown when there's >1 gap. With one
              gap, the per-row buttons already cover everything and a
              bulk button would just be visual noise. */}
          {allGaps.length > 1 && (onApproveBatch || onRejectBatch) && (
            <div className="mobile-gaps__bulk">
              {onApproveBatch && (
                <button
                  type="button"
                  className="mobile-gaps__bulk-btn mobile-gaps__bulk-btn--approve"
                  onClick={() => void handleBulkApprove()}
                  disabled={bulkBusy !== null}
                  aria-label="Approve every pending proposal"
                >
                  {bulkBusy === 'approve' ? '…' : bulkApproveLabel}
                </button>
              )}
              {onRejectBatch && (
                <button
                  type="button"
                  className="mobile-gaps__bulk-btn mobile-gaps__bulk-btn--reject"
                  onClick={() => void handleBulkReject()}
                  disabled={bulkBusy !== null}
                  aria-label="Dismiss every pending proposal"
                >
                  {bulkBusy === 'reject' ? '…' : `Dismiss all (${allGaps.length})`}
                </button>
              )}
            </div>
          )}
          {hasGroupedPriority ? (
            (Object.keys(gapGroups) as PriorityKey[])
              .filter((p) => gapGroups[p].length > 0)
              .map((p) => (
                <div key={p} className="mobile-gaps__group">
                  <div className="mobile-gaps__group-label mono">
                    {priorityLabel(p)} · {gapGroups[p].length}
                  </div>
                  {gapGroups[p].map((g) => (
                    <GapRow
                      key={g.card_id}
                      gap={g}
                      onPeek={onPeek}
                      onApprove={onApprove}
                      onReject={onReject}
                      onDiscuss={onDiscuss}
                    />
                  ))}
                </div>
              ))
          ) : (
            allGaps.map((g) => (
              <GapRow
                key={g.card_id}
                gap={g}
                onPeek={onPeek}
                onApprove={onApprove}
                onReject={onReject}
                onDiscuss={onDiscuss}
              />
            ))
          )}
        </div>
      )}

      {/* Section 2: STALE RIPPLE */}
      {staleRipple.length > 0 && (
        <div className="mobile-gaps__section">
          <h2 className="mobile-gaps__section-title">STALE RIPPLE</h2>
          <div className="mobile-gaps__section-hint">
            cards depending on a decision that just changed — review or revise.
          </div>
          {staleRipple.map((c) => (
            <article
              key={c.card_id}
              className="mobile-gaps__stale-row"
              onClick={() => onPeek(c.card_id)}
            >
              <div className="mobile-gaps__stale-rowhead">
                <KindPill kind={c.kind} />
                <span className="mobile-gaps__stale-tag mono">STALE</span>
              </div>
              <h3 className="mobile-gaps__stale-title">{deriveTitle(c)}</h3>
            </article>
          ))}
        </div>
      )}

      {/* Section 3: FRAME GAPS */}
      {frameGaps.length > 0 && (
        <div className="mobile-gaps__section">
          <h2 className="mobile-gaps__section-title">FRAME GAPS</h2>
          <div className="mobile-gaps__section-hint">
            empty MECE nodes in the active frame.
          </div>
          {frameGaps.map((fg) => (
            <article
              key={`${fg.frameId}::${fg.node.path}`}
              className="mobile-gaps__frame-row"
            >
              <header className="mobile-gaps__frame-rowhead">
                <span className="mobile-gaps__frame-name mono">
                  {fg.frameName}
                </span>
                <span className="mobile-gaps__frame-path mono">
                  {fg.node.path}
                </span>
              </header>
              <h3 className="mobile-gaps__frame-label">{fg.node.label}</h3>
              {fg.node.description && (
                <p className="mobile-gaps__frame-desc">{fg.node.description}</p>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

function OpenQuestionRow({
  card,
  onPeek,
  onAnswer,
  onArchive,
}: {
  card: Card
  onPeek: (id: string) => void
  onAnswer?: (cardId: string) => void
  onArchive?: (cardId: string) => void | Promise<void>
}) {
  const [busy, setBusy] = useState<null | 'archive'>(null)
  const display = deriveTitle(card)
  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onArchive || busy) return
    setBusy('archive')
    try {
      await onArchive(card.card_id)
    } finally {
      setBusy(null)
    }
  }
  const handleAnswer = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onAnswer) return
    onAnswer(card.card_id)
  }
  return (
    <article
      className="mobile-question"
      onClick={() => onPeek(card.card_id)}
      style={{ opacity: busy ? 0.6 : 1 }}
    >
      <header className="mobile-question__head">
        <span className="mobile-question__glyph mono" aria-hidden>
          ? question
        </span>
        <span className="mobile-question__id mono">{card.card_id}</span>
      </header>
      <h3 className="mobile-question__title">{display}</h3>
      {card.body.trim() && card.body.trim() !== card.title?.trim() && (
        <p className="mobile-question__preview">
          {card.body.length > 140 ? card.body.slice(0, 140).trimEnd() + '…' : card.body}
        </p>
      )}
      <div
        className="mobile-question__actions"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mobile-question__btn mobile-question__btn--answer"
          onClick={handleAnswer}
          disabled={!onAnswer || busy !== null}
          aria-label={`Answer ${card.card_id}`}
        >
          answer ↗
        </button>
        <button
          type="button"
          className="mobile-question__btn mobile-question__btn--archive"
          onClick={handleArchive}
          disabled={!onArchive || busy !== null}
          aria-label={`Archive ${card.card_id}`}
        >
          {busy === 'archive' ? '…' : 'archive'}
        </button>
      </div>
    </article>
  )
}

function GapRow({
  gap,
  onPeek,
  onApprove,
  onReject,
  onDiscuss,
}: {
  gap: Card
  onPeek: (id: string) => void
  onApprove: (id: string) => void | Promise<void>
  onReject: (id: string) => void | Promise<void>
  onDiscuss: (id: string) => void
}) {
  const reason =
    typeof gap.extra?.['ambiguity_reason'] === 'string'
      ? (gap.extra['ambiguity_reason'] as string)
      : null
  const blocks = Array.isArray(gap.extra?.['blocks'])
    ? (gap.extra['blocks'] as unknown[]).filter(
        (x): x is string => typeof x === 'string',
      )
    : []
  const gapKind =
    typeof gap.extra?.['gap_kind'] === 'string'
      ? (gap.extra['gap_kind'] as string)
      : null
  const isConflict = gapKind === 'conflict'
  // Effective destination kind drives both the per-row tag AND the
  // smart approve label. Mirrors GapLedger's `proposedKind` rule so
  // mobile and desktop agree on what'll happen post-approve.
  const proposedKind = effectiveProposedKind(gap)
  const approveLabel =
    proposedKind === 'question' ? 'Add to OPEN' : 'Add to cards'

  return (
    <article
      className="mobile-gap"
      onClick={() => onPeek(gap.card_id)}
    >
      <header className="mobile-gap__head">
        <span className="mobile-gap__glyph" aria-hidden>◌</span>
        <h3 className="mobile-gap__title">{deriveTitle(gap)}</h3>
        <span className="mobile-gap__proposed mono">→ {proposedKind}</span>
      </header>
      {gap.body.trim() && (
        <p className="mobile-gap__preview">
          {gap.body.length > 140 ? gap.body.slice(0, 140).trimEnd() + '…' : gap.body}
        </p>
      )}
      {reason && <p className="mobile-gap__why">why: {reason}</p>}
      {blocks.length > 0 && (
        <div className="mobile-gap__blocks mono">
          blocks: {blocks.join(', ')}
        </div>
      )}
      <div
        className="mobile-gap__actions"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Conflict gaps don't promote — they're resolved by editing one
            of the cited blocks. Hide Approve for them; Discuss + Reject
            stay since both still apply. */}
        {!isConflict && (
          <button
            type="button"
            className="mobile-gap__btn mobile-gap__btn--approve"
            onClick={() => void onApprove(gap.card_id)}
            aria-label={`Approve ${gap.card_id} as ${proposedKind}`}
          >
            {approveLabel}
          </button>
        )}
        <button
          type="button"
          className="mobile-gap__btn mobile-gap__btn--discuss"
          onClick={() => onDiscuss(gap.card_id)}
          aria-label={`Discuss ${gap.card_id}`}
        >
          💬 Discuss
        </button>
        <button
          type="button"
          className="mobile-gap__btn mobile-gap__btn--reject"
          onClick={() => void onReject(gap.card_id)}
          aria-label={`Reject ${gap.card_id}`}
        >
          ✗ Reject
        </button>
      </div>
    </article>
  )
}
